import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PermissionsContext = createContext();

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Function لتحميل الصلاحيات من localStorage
  const loadPermissions = useCallback(() => {
    try {
      const storedPermissions = localStorage.getItem('userPermissions');
      const storedRole = localStorage.getItem('userRole');
      
      console.log('📦 Loading permissions from localStorage');
      
      if (storedPermissions) {
        const parsedPermissions = JSON.parse(storedPermissions);
        setPermissions(parsedPermissions);
        console.log('✅ Loaded', parsedPermissions.length, 'permissions');
      } else {
        console.warn('⚠️ No permissions found in localStorage');
        setPermissions([]);
      }
      
      if (storedRole) {
        setUserRole(storedRole);
        console.log('✅ Role loaded:', storedRole);
      } else {
        console.warn('⚠️ No role found in localStorage');
        setUserRole(null);
      }
    } catch (error) {
      console.error('❌ Error loading permissions:', error);
      setPermissions([]);
      setUserRole(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ✅ تحميل الصلاحيات عند mount
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // ✅ الاستماع للـ Custom Events
  useEffect(() => {
    const handlePermissionsUpdated = (event) => {
      console.log('🔔 Permissions updated event received');
      const { permissions: newPermissions, role: newRole } = event.detail;
      
      console.log('📊 Updating state with', newPermissions.length, 'permissions');
      setPermissions(newPermissions);
      setUserRole(newRole);
      
      console.log('✅ Context state updated successfully');
    };

    const handlePermissionsCleared = () => {
      console.log('🔔 Permissions cleared event received');
      setPermissions([]);
      setUserRole(null);
    };

    window.addEventListener('permissionsUpdated', handlePermissionsUpdated);
    window.addEventListener('permissionsCleared', handlePermissionsCleared);

    return () => {
      window.removeEventListener('permissionsUpdated', handlePermissionsUpdated);
      window.removeEventListener('permissionsCleared', handlePermissionsCleared);
    };
  }, []);

  const savePermissions = useCallback((newPermissions, role) => {
    try {
      console.log('💾 Saving', newPermissions.length, 'permissions');
      
      localStorage.setItem('userPermissions', JSON.stringify(newPermissions));
      localStorage.setItem('userRole', role);
      setPermissions(newPermissions);
      setUserRole(role);
      
      console.log('✅ Permissions saved successfully');
    } catch (error) {
      console.error('❌ Error saving permissions:', error);
    }
  }, []);

  const hasPermission = useCallback((permission) => {
    return permissions.includes(permission);
  }, [permissions]);

  const hasAnyPermission = useCallback((permissionList) => {
    return permissionList.some(permission => permissions.includes(permission));
  }, [permissions]);

  const hasAllPermissions = useCallback((permissionList) => {
    return permissionList.every(permission => permissions.includes(permission));
  }, [permissions]);

  const clearPermissions = useCallback(() => {
    console.log('🗑️ Clearing permissions');
    localStorage.removeItem('userPermissions');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('userEmail');
    setPermissions([]);
    setUserRole(null);
  }, []);

  const value = {
    permissions,
    userRole,
    isLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    savePermissions,
    clearPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};